import React from 'react';
import { motion } from 'framer-motion';
import { Wine, Clock, MapPin, Phone, Sparkles } from 'lucide-react';
import { BAR_LOUNGE, PROPERTY_IMAGES } from '../utils/constants';
import ScrollReveal from './ScrollReveal';

const BarLoungeSection = () => {
  return (
    <section id="dining" className="relative py-24 bg-navy-900 text-white overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={PROPERTY_IMAGES.jalsaRetreat.dining}
          alt="101 Bar & Lounge"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900 via-navy-900/95 to-navy-900" />
      </div>

      {/* Decorative Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <ScrollReveal direction="up">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-6">
              <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-400" />
              <Wine className="mx-4 w-6 h-6 text-gold-400" />
              <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-400" />
            </div>
            
            <h2 className="text-5xl md:text-6xl font-serif mb-4">
              {BAR_LOUNGE.name}
            </h2>
            
            <p className="text-xl text-gold-400 font-light italic mb-4">
              "{BAR_LOUNGE.tagline}"
            </p>

            <div className="flex items-center justify-center space-x-2 text-gray-400">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">Exclusive to {BAR_LOUNGE.location}</span>
            </div>
          </div>
        </ScrollReveal>

        {/* Content Grid */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            {/* Left: Description */}
            <ScrollReveal direction="left">
              <div className="space-y-8">
                <p className="text-lg text-gray-300 leading-relaxed">
                  {BAR_LOUNGE.description}
                </p>

                {/* Features */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-serif text-gold-400 mb-4">Highlights</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {BAR_LOUNGE.features.map((feature, idx) => (
                      <motion.div 
                        key={idx} 
                        className="flex items-center space-x-3 group"
                        whileHover={{ x: 5 }}
                      >
                        <div className="w-2 h-2 bg-gold-400 rotate-45 group-hover:scale-125 transition-transform duration-300" />
                        <span className="text-gray-300 group-hover:text-gold-400 transition-colors duration-300">
                          {feature}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Info Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="bg-white/5 backdrop-blur-sm border border-gold-400/20 rounded-lg p-4 hover:border-gold-400/40 transition-colors duration-300"
                  >
                    <Clock className="w-6 h-6 text-gold-400 mb-2" />
                    <p className="text-sm text-gray-400 mb-1">Timings</p>
                    <p className="text-white font-semibold">{BAR_LOUNGE.timings}</p>
                  </motion.div>
                  
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="bg-white/5 backdrop-blur-sm border border-gold-400/20 rounded-lg p-4 hover:border-gold-400/40 transition-colors duration-300"
                  >
                    <Phone className="w-6 h-6 text-gold-400 mb-2" />
                    <p className="text-sm text-gray-400 mb-1">Reservations</p>
                    <p className="text-white font-semibold text-sm">{BAR_LOUNGE.contact}</p>
                  </motion.div>
                </div>
              </div>
            </ScrollReveal>

            {/* Right: Image Showcase */}
            <ScrollReveal direction="right">
              <div className="relative">
                <motion.div 
                  className="relative group"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Main Image */}
                  <div className="relative overflow-hidden rounded-lg">
                    <img
                      src={BAR_LOUNGE.images?.hero || PROPERTY_IMAGES.common.dining}
                      alt="101 Bar & Lounge Interior"
                      className="w-full h-[400px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gold-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 border-t-2 border-r-2 border-gold-400 opacity-50" />
                  <div className="absolute -bottom-4 -left-4 w-24 h-24 border-b-2 border-l-2 border-gold-400 opacity-50" />
                </motion.div>

                {/* Floating Badge */}
                <motion.div 
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-gold-400 text-navy-900 px-8 py-4 rounded-full shadow-2xl"
                >
                  <div className="flex items-center space-x-2">
                    <Sparkles className="w-5 h-5" />
                    <span className="font-semibold tracking-wider">EXCLUSIVE</span>
                  </div>
                </motion.div>
              </div>
            </ScrollReveal>
          </div>

          {/* Bottom CTA */}
          <ScrollReveal direction="up" delay={0.2}>
            <div className="text-center pt-12">
              <p className="text-gray-400 mb-6 text-lg">
                Experience the finest selection of spirits and cocktails
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-gold-400 text-navy-900 hover:bg-white transition-all duration-300 font-semibold tracking-wider rounded shadow-lg hover:shadow-gold-400/50"
                >
                  RESERVE A TABLE
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 border-2 border-gold-400 text-gold-400 hover:bg-gold-400 hover:text-navy-900 transition-all duration-300 font-semibold tracking-wider rounded"
                >
                  VIEW MENU
                </motion.button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* Decorative Bottom Border */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent" />
    </section>
  );
};

export default BarLoungeSection;
